import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AlertIcon } from 'src/assets/tailwind/alert-circle.svg';
import { ReactComponent as CheckIcon } from 'src/assets/tailwind/check-circle.svg';
import { ReactComponent as MinimizeIcon } from 'src/assets/tailwind/minimize-icon.svg';
import { ReactComponent as PlayIcon } from 'src/assets/tailwind/play.svg';
import { ReactComponent as XIcon } from 'src/assets/tailwind/x-close.svg';
import { Button } from 'src/components/ui/button';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'src/components/ui/dialog';
import { FileList } from 'src/components/ui/file-list';
import { Icon } from 'src/components/ui/icon';
import { Progress } from 'src/components/ui/progress';
import { Tab } from 'src/components/ui/tab';
import { Typography } from 'src/components/ui/typography';
import { FileComparisonDialog } from 'src/content/DataLibrary/UploadSession/FileComparisonDialog';
import { UploadConflictDialog } from 'src/content/DataLibrary/UploadSession/UploadConflictDialog';
import UploadSessionConflictModal from 'src/content/DataLibrary/UploadSession/UploadSessionConflictModal';
import { ConfirmationDialog } from 'src/components/ui/ConfirmationDialog';
import {
  useUploadWidget,
  type UploadProgress
} from 'src/context/UploadWidgetContext';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { cn } from 'src/utils/utils';

import { DropzoneArea } from './DropzoneArea';
import { useUploadCoordinator } from './useUploadCoordinator';

interface TabItem {
  text: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

type DataLibraryUploadDropzoneProps = {
  tabs: TabItem[];
  toggleUploadModal: () => void;
};

export default function DataLibraryUploadDropzone({
  tabs = [],
  toggleUploadModal
}: DataLibraryUploadDropzoneProps) {
  const { t } = useTranslation();
  const {
    files,
    addFiles,
    removeFile,
    totalProgress,
    successCount,
    failedCount,
    droppedCount,
    handleCancelAll,
    retryFailedUploads,
    isPaused,
    resumePausedSession,
    isResumeUpload,
    confirmResumeSelection,
    scanProgress,
    serverStateComparison,
    serverStateDestinationPath,
    isComparisonDialogOpen,
    isServerStateLoading,
    serverStateOverwrite,
    setServerStateOverwrite,
    handleComparisonConfirm,
    handleComparisonCancel,
    serverStateConflict,
    isConflictDialogOpen,
    handleConflictClose,
    isSessionConflictOpen,
    setIsSessionConflictOpen
  } = useUploadCoordinator();

  const { minimizeUpload, isUploadFileAllowed, isMinimized } =
    useUploadWidget();

  const [showConfirm, setShowConfirm] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const isUploading =
    totalProgress > 0 && totalProgress < 100 && files.length > 0;

  const handleClose = () => {
    handleCancelAll();
    toggleUploadModal();
  };

  const isUploadSettled =
    totalProgress === 100 && !!(failedCount || droppedCount);

  const attemptAction = (action: () => void, confirmWhenSettled = false) => {
    if (isUploading || (confirmWhenSettled && isUploadSettled)) {
      pendingActionRef.current = action;
      setShowConfirm(true);
    } else {
      action();
    }
  };

  const attemptClose = () => attemptAction(handleClose);

  const confirmAbort = () => {
    setShowConfirm(false);
    const action = pendingActionRef.current ?? handleClose;
    pendingActionRef.current = null;
    action();
  };

  return (
    <>
      {!isSessionConflictOpen && (
        <DialogContent
          hideDefaultClose
          onPointerDownOutside={(e) => {
            e.preventDefault();
            if (minimizeUpload) {
              minimizeUpload();
            } else {
              attemptClose();
            }
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            if (minimizeUpload) {
              minimizeUpload();
            } else {
              attemptClose();
            }
          }}
        >
          <DialogHeader className="tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center">
            <DialogTitle>{t(T.uploadFiles)}</DialogTitle>
            <div className="tw:flex tw:items-center tw:gap-xs">
              {!!minimizeUpload && files.length > 0 && (
                <Button
                  leftIcon={MinimizeIcon}
                  size="small"
                  hierarchy="ghost"
                  iconOnly
                  className="tw:w-2xl"
                  onClick={minimizeUpload}
                />
              )}
              <Button
                leftIcon={XIcon}
                size="small"
                hierarchy="ghost"
                iconOnly
                className="tw:w-2xl"
                onClick={attemptClose}
              />
            </div>
          </DialogHeader>
          <div className="tw:flex tw:flex-col ">
            <div
              className={cn(
                'tw:flex tw:flex-wrap tw:gap-2xs tw:px-2xl tw:py-s tw:border-b tw:border-b-border-secondary'
              )}
            >
              {!!tabs?.length &&
                tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    {...tab}
                    onClick={() => attemptAction(tab.onClick, true)}
                  />
                ))}
            </div>
            <div className="tw:p-2xl tw:h-[432px] tw:flex tw:w-full  tw:gap-l ">
              <div className="tw:w-full tw:h-full tw:min-h-0 tw:flex tw:flex-col">
                {!files.length && !scanProgress && (
                  <DropzoneArea
                    onDrop={addFiles}
                    isUploadFileAllowed={isUploadFileAllowed}
                  />
                )}

                {!files.length && scanProgress && (
                  <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-l tw:h-full tw:w-full">
                    <Typography
                      variant="h5"
                      colorClass="tw:text-text-primary"
                      align="center"
                    >
                      {
                        t(T.scanningFilesCount, {
                          processed: scanProgress.processed,
                          total: scanProgress.total
                        }) as string
                      }
                    </Typography>
                    <Progress
                      value={Math.round(
                        (scanProgress.processed / scanProgress.total) * 100
                      )}
                    />
                  </div>
                )}

                {files.length > 0 && (
                  <div className="tw:flex tw:flex-col tw:gap-l tw:w-full tw:h-full tw:min-h-0">
                    {totalProgress === 100 ? (
                      <>
                        {!!successCount && (
                          <Typography
                            variant="h5"
                            colorClass="tw:text-text-primary"
                            className="tw:flex tw:items-center tw:gap-s"
                          >
                            <Icon
                              icon={CheckIcon}
                              size={20}
                              colorClass="tw:text-icon-success"
                            />
                            <span>
                              {t(T.documentsUploadedSuccessfully, {
                                count: successCount
                              })}
                            </span>
                          </Typography>
                        )}
                        {!!(failedCount || droppedCount) && (
                          <Typography
                            variant="h5"
                            colorClass="tw:text-text-primary"
                            className="tw:flex tw:items-center tw:gap-s"
                          >
                            <Icon
                              icon={AlertIcon}
                              size={20}
                              colorClass="tw:text-icon-warning"
                            />
                            {t(T.documentUploadFailed, {
                              count: failedCount + droppedCount
                            })}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="h5"
                        colorClass="tw:text-text-primary"
                        align="center"
                        className="tw:self-center"
                      >
                        {t(T.uploadingFilesCount, { count: files?.length })}
                      </Typography>
                    )}

                    {totalProgress < 100 ? (
                      <div className="tw:flex tw:items-center tw:gap-l">
                        <Progress value={totalProgress} />
                        <Typography
                          variant="h6"
                          colorClass="tw:text-text-primary"
                        >
                          {totalProgress}%
                        </Typography>
                      </div>
                    ) : null}

                    <div className="tw:flex-1 tw:min-h-0">
                      <FileList files={files} onRemove={removeFile} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
              <Button
                hierarchy="destructive"
                size="small"
                text={t(T.cancel)}
                onClick={attemptClose}
              />
              <div className="tw:flex tw:items-center tw:gap-xl">
                {totalProgress === 100 && (
                  <Button
                    hierarchy="secondary"
                    size="small"
                    text={t(T.done)}
                    onClick={toggleUploadModal}
                  />
                )}

                {!!(isResumeUpload && files.length > 0) && (
                  <Button
                    leftIcon={PlayIcon}
                    hierarchy="primary"
                    text={t(T.resume)}
                    size="small"
                    onClick={confirmResumeSelection}
                  />
                )}

                {!!(
                  !isResumeUpload &&
                  isPaused &&
                  files.length > 0 &&
                  totalProgress < 100
                ) && (
                  <Button
                    leftIcon={PlayIcon}
                    hierarchy="primary"
                    text={t(T.resume)}
                    size="small"
                    onClick={resumePausedSession}
                  />
                )}

                {!!failedCount && (
                  <Button
                    hierarchy="primary"
                    text={t(T.retryFailedDocument)}
                    size="small"
                    disabled={!failedCount}
                    onClick={retryFailedUploads}
                  />
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      )}
      <ConfirmationDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmAbort}
        title={t(T.abortUpload)}
        description={t(T.abortUploadDescription)}
        confirmText={t(T.yesAbortAll)}
        cancelText={t(T.noKeepUploading)}
      />
      {serverStateComparison && (
        <FileComparisonDialog
          isOpen={isComparisonDialogOpen}
          onClose={() => {
            handleComparisonCancel();
            attemptClose();
          }}
          onConfirm={handleComparisonConfirm}
          comparison={serverStateComparison}
          destinationPath={serverStateDestinationPath}
          overwrite={serverStateOverwrite}
          onOverwriteChange={setServerStateOverwrite}
          isLoading={isServerStateLoading}
        />
      )}
      {serverStateConflict && (
        <UploadConflictDialog
          isOpen={isConflictDialogOpen}
          onClose={handleConflictClose}
          conflict={serverStateConflict}
          destinationPath={serverStateDestinationPath}
        />
      )}
      <UploadSessionConflictModal
        open={isSessionConflictOpen}
        onOpenChange={(open) => {
          setIsSessionConflictOpen(open);
          if (!open) {
            handleClose();
          }
        }}
      />
    </>
  );
}
